import { Injectable } from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { Subject, Observable } from 'rxjs';
import { NotificationService } from 'src/app/shared/notification/notification.service';
import isAfter from 'validator/es/lib/isAfter';
import isDate from 'validator/es/lib/isDate';
import {
    CardConnectManualEntryData,
    CardConnectProfileAccountData,
} from '../card-connect.interface';
import { CardOnFile } from '../payment-information/card-on-file';
import { StripePaymentData } from '../stripe.interface';
import { StripeElements } from '@stripe/stripe-js';
@Injectable()
export class CheckoutFormService {
    form: FormGroup;
    private formSubmitted = new Subject<void>();
    private stripeFormSubmitted = new Subject<void>();
    private initiateStripeFormSubject = new Subject<void>();
    private stripePaymentElements: StripeElements;
    private stripePaymentData: StripePaymentData;
    constructor(
        private fb: FormBuilder,
        private notificationService: NotificationService
    ) {}

    createNewCardForm(linkToUserAccount: boolean): FormGroup {
        this.form = this.fb.group({
            creditCardToken: ['', [Validators.required]],
            creditCardExpiration: [
                '',
                [
                    Validators.required,
                    Validators.minLength(4),
                    Validators.maxLength(4),
                    this.creditCardExpirationValidator(),
                ],
            ],
            creditCardCvv: [
                '',
                [
                    Validators.required,
                    Validators.minLength(3),
                    Validators.maxLength(4),
                ],
            ],
            creditCardZip: [
                '',
                [
                    Validators.required,
                    Validators.minLength(5),
                    Validators.maxLength(5),
                ],
            ],
            creditCardClientName: ['', [Validators.required]],
            saveCardOnFile: [false],
            linkToUserAccount: [linkToUserAccount],
        });

        return this.form;
    }

    createCardsOnFileForm(): FormGroup {
        return (this.form = this.fb.group({
            cardOnFile: ['', [Validators.required]],
            linkToUserAccount: [true],
        }));
    }

    createStripeForm(linkToUserAccount: boolean): FormGroup {
        this.form = this.fb.group({
            saveCard: [false],
            linkToUserAccount: [linkToUserAccount],
        });

        return this.form;
    }

    selectCardOnFile(cardOnFile: CardOnFile) {
        this.form.controls.cardOnFile.setValue(cardOnFile.id);
    }

    submitForm() {
        this.formSubmitted.next();
    }

    formSubmitted$(): Observable<void> {
        return this.formSubmitted.asObservable();
    }
    submitStripeForm() {
        this.stripeFormSubmitted.next();
    }
    stripeFormSubmitted$(): Observable<void> {
        return this.stripeFormSubmitted.asObservable();
    }
    destroyForm(): void {
        this.form = null;
        this.stripePaymentElements = null;
        this.stripePaymentData = null;
    }

    formIsValid(): boolean {
        if (!this.form) {
            this.notificationService.showFailure(
                'Please log in or choose to checkout as a guest to continue.'
            );
            return false;
        }

        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return false;
        }

        return true;
    }

    getData(): CardConnectManualEntryData | CardConnectProfileAccountData {
        if (this.form.controls.cardOnFile) {
            return {
                id: this.form.controls.cardOnFile.value,
            };
        } else {
            const expirationVals = this.getExpirationDateValueArray(
                this.form.controls.creditCardExpiration.value
            );

            return {
                creditCardToken: this.form.controls.creditCardToken.value,
                creditCardExpirationMonth: expirationVals[0],
                creditCardExpirationYear: expirationVals[1],
                creditCardCvv: this.form.controls.creditCardCvv.value,
                creditCardClientName: this.form.controls.creditCardClientName
                    .value,
                creditCardZipCode: this.form.controls.creditCardZip.value,
                saveCardOnFile: this.form.controls.saveCardOnFile.value,
            };
        }
    }

    private creditCardExpirationValidator(): ValidatorFn {
        return (control: FormControl): ValidationErrors | null => {
            const expirationString = control.value as string;

            if (expirationString?.length) {
                const expirationVals = this.getExpirationDateValueArray(
                    expirationString
                );
                const month = expirationVals[0];
                const year = '20' + expirationVals[1];
                const dateToValidate = `${year}/${month}/01`;
                const now = new Date();

                const isDateValid = isDate(dateToValidate);
                const isCardCurrent =
                    (now.getMonth() + 1 === parseInt(month) &&
                        now.getFullYear() === parseInt(year)) ||
                    isAfter(dateToValidate);

                return isDateValid && isCardCurrent
                    ? null
                    : { invalidExpiration: true };
            }
        };
    }

    private getExpirationDateValueArray(expirationString) {
        return expirationString?.match(/.{1,2}/g);
    }
    setStripePaymentElements(elements: StripeElements): void {
        this.stripePaymentElements = elements;
    }
    getStripePaymentElements(): StripeElements {
        return this.stripePaymentElements;
    }

    getStripePaymentData(): StripePaymentData {
        if (this.form?.get('cardOnFile')?.value) {
            return {
                paymentMethodId: this.form.get('cardOnFile').value,
                paymentMethodType: 'card',
                saveCard: false,
            };
        }
        return {
            paymentMethodId: '',
            paymentMethodType: 'card',
            saveCard: this.form?.get('saveCard')?.value || false,
        };
    }

    clearStripeElements(): void {
        this.stripePaymentElements = null;
    }

    isUsingSavedCard(): boolean {
        return !!this.form?.get('cardOnFile')?.value;
    }

    resetForNewPurchase(): void {
        this.form = null;
        this.stripePaymentElements = null;
        this.stripePaymentData = null;
    }
}
